export const supportedLocales = ['cs', 'en', 'de'] as const;

export type Locale = (typeof supportedLocales)[number];

type ContentCard = {
  title: string;
  text: string;
};

export type PublicCopy = {
  localeName: string;
  meta: {
    title: string;
    description: string;
  };
  nav: {
    solutions: string;
    howItWorks: string;
    security: string;
    contact: string;
    login: string;
    app: string;
  };
  hero: {
    eyebrow: string;
    title: string;
    titleAccent: string;
    text: string;
    primaryAction: string;
    secondaryAction: string;
    proofPoints: string[];
    demoLabel: string;
    cameraLive: string;
    person: string;
    vehicle: string;
    eventTitle: string;
    eventText: string;
  };
  solutions: {
    eyebrow: string;
    title: string;
    text: string;
    items: ContentCard[];
  };
  steps: {
    eyebrow: string;
    title: string;
    text: string;
    items: ContentCard[];
  };
  security: {
    eyebrow: string;
    title: string;
    text: string;
    items: ContentCard[];
  };
  faq: {
    eyebrow: string;
    title: string;
    text: string;
    items: ContentCard[];
  };
  contact: {
    title: string;
    text: string;
    appAction: string;
  };
  footer: string;
};

export type LoginCopy = {
  metaTitle: string;
  metaDescription: string;
  back: string;
  title: string;
  intro: string;
  login: string;
  password: string;
  tokenDuration: string;
  tokenDays: { one: string; seven: string; thirty: string; ninety: string; year: string };
  continue: string;
  emailSetupTitle: string;
  emailSetupIntro: string;
  email: string;
  emailConfirmation: string;
  emailSetupAction: string;
  verificationTitle: string;
  verificationIntro: string;
  verificationValidity: string;
  code: string;
  verify: string;
  backToLogin: string;
  connectionError: string;
};

export const publicCopy: Record<Locale, PublicCopy> = {
  cs: {
    localeName: 'Čeština',
    meta: {
      title: 'TORLIN | Inteligentní kamerový systém',
      description:
        'Český inteligentní kamerový systém pro ochranu majetku, analýzu provozu a automatizaci.',
    },
    nav: {
      solutions: 'Řešení',
      howItWorks: 'Jak to funguje',
      security: 'Soukromí',
      contact: 'Kontakt',
      login: 'Přihlásit se',
      app: 'Do aplikace',
    },
    hero: {
      eyebrow: 'INTELIGENTNÍ KAMEROVÝ SYSTÉM',
      title: 'Vidí víc.',
      titleAccent: 'Pomáhá rozhodovat.',
      text: 'TORLIN mění běžné kamery v chytrý systém, který chrání majetek, analyzuje provoz a pomáhá automatizovat procesy.',
      primaryAction: 'Probrat vaše řešení',
      secondaryAction: 'K čemu TORLIN slouží',
      proofPoints: ['Český vývoj', 'Více než 250 realizací', 'Napojení na stávající kamery'],
      demoLabel: 'Ukázka inteligentní analýzy kamerového obrazu',
      cameraLive: 'KAMERA 04 · ŽIVĚ',
      person: 'OSOBA · 98%',
      vehicle: 'VOZIDLO · 96%',
      eventTitle: 'Událost vyhodnocena',
      eventText: 'Oprávněný pohyb · bez upozornění',
    },
    solutions: {
      eyebrow: 'ŘEŠENÍ',
      title: 'Jedna technologie. Mnoho způsobů využití.',
      text: 'TORLIN není hotová krabice s jednou funkcí. Každé nasazení stavíme podle toho, co má systém poznat, změřit nebo ochránit.',
      items: [
        {
          title: 'Domov a objekty',
          text: 'TORLIN sleduje dům, chatu, zahradu nebo stavbu. Rozpozná nečekaný pohyb a upozorní vás ve chvíli, kdy je to důležité.',
        },
        {
          title: 'Retail a návštěvnost',
          text: 'Počítání zákazníků, analýza pohybu, front a vytíženosti prostoru. Srozumitelná data pro lepší rozhodování.',
        },
        {
          title: 'Bezpečnost',
          text: 'Nepřetržitý dohled, rychlé vyhodnocení událostí a okamžité upozornění odpovědných osob nebo ostrahy.',
        },
        {
          title: 'Průmysl',
          text: 'Kontrola jakosti, čtení etiket a automatizace procesů. Kamerový systém se stává součástí výroby.',
        },
      ],
    },
    steps: {
      eyebrow: 'JAK TO FUNGUJE',
      title: 'Od obrazu k užitečné informaci.',
      text: 'Kamery samy pouze zaznamenávají. TORLIN přidává kontext, vyhodnocení a reakci.',
      items: [
        {
          title: 'Připojíme obraz',
          text: 'Využijeme vaše stávající IP kamery, nebo navrhneme nové řešení podle prostoru a cíle.',
        },
        {
          title: 'Neuron vyhodnotí situaci',
          text: 'Lokální řídicí jednotka analyzuje obraz a rozpozná události, které mají pro vás význam.',
        },
        {
          title: 'Vy dostanete výsledek',
          text: 'Upozornění, přehledy a data najdete v aplikaci TORLIN na webu i v mobilu.',
        },
      ],
    },
    security: {
      eyebrow: 'SOUKROMÍ A BEZPEČNOST',
      title: 'Obrazová data mají zůstat pod kontrolou.',
      text: 'Návrh systému přizpůsobíme citlivosti provozu. Analýza může běžet lokálně a přístup k událostem chráníme stejně důsledně jako samotný objekt.',
      items: [
        {
          title: 'Řízený přístup',
          text: 'Každý uživatel vidí pouze služby a data, ke kterým má oprávnění.',
        },
        {
          title: 'Volitelné ukládání',
          text: 'Lokální úložiště, bezpečná cloudová záloha nebo jejich kombinace.',
        },
        {
          title: 'Soukromí od návrhu',
          text: 'Rozsah analýzy nastavujeme podle účelu, nikoli podle toho, co všechno technologie dokáže.',
        },
      ],
    },
    faq: {
      eyebrow: 'ČASTÉ OTÁZKY',
      title: 'Nejdřív pochopit problém. Potom navrhnout systém.',
      text: 'Každý provoz je jiný. Tohle jsou odpovědi na otázky, kterými obvykle začínáme.',
      items: [
        {
          title: 'Musím měnit stávající kamery?',
          text: 'Ve většině případů ne. Nejprve ověříme kompatibilitu a navrhneme nejjednodušší cestu zapojení.',
        },
        {
          title: 'Kde se zpracovává obraz?',
          text: 'Analýza může probíhat lokálně na jednotce TORLIN Neuron. Způsob ukládání a zálohování nastavíme podle vašich potřeb.',
        },
        {
          title: 'Je řešení jen pro bezpečnost?',
          text: 'Není. Stejná technologie pomáhá s návštěvností, výrobou, kontrolou kvality i dalšími procesy na míru.',
        },
      ],
    },
    contact: {
      title: 'Co by měl TORLIN vidět u vás?',
      text: 'Popište nám prostor, současné kamery a výsledek, který potřebujete. Navrhneme smysluplný další krok bez zbytečné techniky.',
      appAction: 'Vstoupit do aplikace',
    },
    footer: 'Český vývoj od HEADSOFT',
  },
  en: {
    localeName: 'English',
    meta: {
      title: 'TORLIN | Intelligent camera system',
      description:
        'An intelligent camera system for property protection, operational analytics and process automation.',
    },
    nav: {
      solutions: 'Solutions',
      howItWorks: 'How it works',
      security: 'Privacy',
      contact: 'Contact',
      login: 'Log in',
      app: 'Open app',
    },
    hero: {
      eyebrow: 'INTELLIGENT CAMERA SYSTEM',
      title: 'Sees more.',
      titleAccent: 'Helps you decide.',
      text: 'TORLIN turns ordinary cameras into an intelligent system that protects property, analyses operations and helps automate processes.',
      primaryAction: 'Discuss your solution',
      secondaryAction: 'What TORLIN can do',
      proofPoints: ['Developed in Czechia', 'More than 250 installations', 'Works with existing cameras'],
      demoLabel: 'Intelligent camera image analysis preview',
      cameraLive: 'CAMERA 04 · LIVE',
      person: 'PERSON · 98%',
      vehicle: 'VEHICLE · 96%',
      eventTitle: 'Event evaluated',
      eventText: 'Authorised movement · no alert',
    },
    solutions: {
      eyebrow: 'SOLUTIONS',
      title: 'One technology. Many ways to use it.',
      text: 'TORLIN is not a one-purpose product in a box. Every installation is designed around what the system needs to detect, measure or protect.',
      items: [
        {
          title: 'Homes and properties',
          text: 'TORLIN monitors a home, holiday house, garden or construction site. It detects unexpected movement and alerts you when it matters.',
        },
        {
          title: 'Retail and footfall',
          text: 'Customer counting, movement, queue and space utilisation analysis. Clear data for better decisions.',
        },
        {
          title: 'Security',
          text: 'Continuous monitoring, rapid event assessment and immediate notification of responsible staff or security services.',
        },
        {
          title: 'Industry',
          text: 'Quality control, label reading and process automation. The camera system becomes part of production.',
        },
      ],
    },
    steps: {
      eyebrow: 'HOW IT WORKS',
      title: 'From an image to useful information.',
      text: 'Cameras only record. TORLIN adds context, evaluation and a response.',
      items: [
        {
          title: 'We connect the video',
          text: 'We use your existing IP cameras or design a new solution to match the space and the objective.',
        },
        {
          title: 'Neuron evaluates the situation',
          text: 'A local control unit analyses the video and recognises events that matter to you.',
        },
        {
          title: 'You receive the result',
          text: 'Alerts, overviews and data are available in the TORLIN web and mobile application.',
        },
      ],
    },
    security: {
      eyebrow: 'PRIVACY AND SECURITY',
      title: 'Your video data should remain under your control.',
      text: 'We adapt the system design to the sensitivity of your operations. Analysis can run locally, while access to events is protected as carefully as the property itself.',
      items: [
        {
          title: 'Controlled access',
          text: 'Each user sees only the services and data they are authorised to access.',
        },
        {
          title: 'Flexible storage',
          text: 'Local storage, secure cloud backup or a combination of both.',
        },
        {
          title: 'Privacy by design',
          text: 'We configure the scope of analysis around its purpose, not around everything the technology could do.',
        },
      ],
    },
    faq: {
      eyebrow: 'FREQUENTLY ASKED QUESTIONS',
      title: 'Understand the problem first. Then design the system.',
      text: 'Every operation is different. These are the questions we usually start with.',
      items: [
        {
          title: 'Do I need to replace my existing cameras?',
          text: 'Usually not. We first verify compatibility and propose the simplest way to connect them.',
        },
        {
          title: 'Where is the video processed?',
          text: 'Analysis can run locally on a TORLIN Neuron unit. Storage and backup are configured to suit your needs.',
        },
        {
          title: 'Is the solution only for security?',
          text: 'No. The same technology supports footfall analysis, manufacturing, quality control and other tailored processes.',
        },
      ],
    },
    contact: {
      title: 'What should TORLIN see for you?',
      text: 'Tell us about the space, your current cameras and the result you need. We will propose a practical next step without unnecessary technology.',
      appAction: 'Open the application',
    },
    footer: 'Developed in Czechia by HEADSOFT',
  },
  de: {
    localeName: 'Deutsch',
    meta: {
      title: 'TORLIN | Intelligentes Kamerasystem',
      description:
        'Ein intelligentes Kamerasystem für Objektschutz, Betriebsanalyse und Prozessautomatisierung.',
    },
    nav: {
      solutions: 'Lösungen',
      howItWorks: 'Funktionsweise',
      security: 'Datenschutz',
      contact: 'Kontakt',
      login: 'Anmelden',
      app: 'Zur Anwendung',
    },
    hero: {
      eyebrow: 'INTELLIGENTES KAMERASYSTEM',
      title: 'Sieht mehr.',
      titleAccent: 'Hilft entscheiden.',
      text: 'TORLIN macht aus gewöhnlichen Kameras ein intelligentes System, das Eigentum schützt, Abläufe analysiert und Prozesse automatisiert.',
      primaryAction: 'Ihre Lösung besprechen',
      secondaryAction: 'Was TORLIN leistet',
      proofPoints: ['Entwickelt in Tschechien', 'Mehr als 250 Installationen', 'Nutzt vorhandene Kameras'],
      demoLabel: 'Vorschau der intelligenten Kamerabildanalyse',
      cameraLive: 'KAMERA 04 · LIVE',
      person: 'PERSON · 98 %',
      vehicle: 'FAHRZEUG · 96 %',
      eventTitle: 'Ereignis ausgewertet',
      eventText: 'Berechtigte Bewegung · keine Meldung',
    },
    solutions: {
      eyebrow: 'LÖSUNGEN',
      title: 'Eine Technologie. Viele Einsatzmöglichkeiten.',
      text: 'TORLIN ist kein fertiges Produkt mit nur einer Funktion. Jede Installation richtet sich danach, was das System erkennen, messen oder schützen soll.',
      items: [
        {
          title: 'Haus und Objekte',
          text: 'TORLIN überwacht Haus, Ferienhaus, Garten oder Baustelle. Das System erkennt unerwartete Bewegungen und meldet sich, wenn es darauf ankommt.',
        },
        {
          title: 'Einzelhandel und Besucherzahlen',
          text: 'Kundenzählung sowie Analyse von Bewegungen, Warteschlangen und Flächenauslastung. Klare Daten für bessere Entscheidungen.',
        },
        {
          title: 'Sicherheit',
          text: 'Lückenlose Überwachung, schnelle Ereignisbewertung und sofortige Benachrichtigung verantwortlicher Personen oder Sicherheitsdienste.',
        },
        {
          title: 'Industrie',
          text: 'Qualitätskontrolle, Etikettenerkennung und Prozessautomatisierung. Das Kamerasystem wird Teil der Produktion.',
        },
      ],
    },
    steps: {
      eyebrow: 'SO FUNKTIONIERT ES',
      title: 'Vom Bild zur nützlichen Information.',
      text: 'Kameras zeichnen nur auf. TORLIN ergänzt Kontext, Bewertung und Reaktion.',
      items: [
        {
          title: 'Wir binden das Bild ein',
          text: 'Wir nutzen Ihre vorhandenen IP-Kameras oder planen eine neue Lösung passend zu Bereich und Ziel.',
        },
        {
          title: 'Neuron bewertet die Situation',
          text: 'Eine lokale Steuereinheit analysiert das Bild und erkennt Ereignisse, die für Sie relevant sind.',
        },
        {
          title: 'Sie erhalten das Ergebnis',
          text: 'Meldungen, Übersichten und Daten finden Sie in der TORLIN Web- und Mobilanwendung.',
        },
      ],
    },
    security: {
      eyebrow: 'DATENSCHUTZ UND SICHERHEIT',
      title: 'Ihre Bilddaten bleiben unter Ihrer Kontrolle.',
      text: 'Wir passen die Systemarchitektur an die Sensibilität Ihres Betriebs an. Die Analyse kann lokal erfolgen, während der Zugriff auf Ereignisse ebenso sorgfältig geschützt wird wie das Objekt selbst.',
      items: [
        {
          title: 'Geregelter Zugriff',
          text: 'Jeder Benutzer sieht nur die Dienste und Daten, für die er berechtigt ist.',
        },
        {
          title: 'Flexible Speicherung',
          text: 'Lokaler Speicher, sichere Cloud-Sicherung oder eine Kombination aus beidem.',
        },
        {
          title: 'Datenschutz von Anfang an',
          text: 'Wir richten den Umfang der Analyse am Zweck aus – nicht an allem, was technisch möglich wäre.',
        },
      ],
    },
    faq: {
      eyebrow: 'HÄUFIGE FRAGEN',
      title: 'Erst das Problem verstehen. Dann das System planen.',
      text: 'Jeder Betrieb ist anders. Mit diesen Fragen beginnen wir in der Regel.',
      items: [
        {
          title: 'Muss ich meine vorhandenen Kameras ersetzen?',
          text: 'Meistens nicht. Wir prüfen zunächst die Kompatibilität und schlagen die einfachste Anbindung vor.',
        },
        {
          title: 'Wo wird das Bild verarbeitet?',
          text: 'Die Analyse kann lokal auf einer TORLIN Neuron Einheit laufen. Speicherung und Sicherung richten wir nach Ihren Anforderungen ein.',
        },
        {
          title: 'Ist die Lösung nur für Sicherheit gedacht?',
          text: 'Nein. Dieselbe Technologie unterstützt Besucheranalysen, Fertigung, Qualitätskontrolle und weitere maßgeschneiderte Prozesse.',
        },
      ],
    },
    contact: {
      title: 'Was soll TORLIN für Sie sehen?',
      text: 'Beschreiben Sie uns den Bereich, Ihre vorhandenen Kameras und das gewünschte Ergebnis. Wir schlagen einen sinnvollen nächsten Schritt ohne unnötige Technik vor.',
      appAction: 'Anwendung öffnen',
    },
    footer: 'Entwickelt in Tschechien von HEADSOFT',
  },
};

export const loginCopy: Record<Locale, LoginCopy> = {
  cs: {
    metaTitle: 'Přihlášení',
    metaDescription: 'Přihlášení do aplikace TORLIN.',
    back: 'Zpět na úvod',
    title: 'Přihlášení',
    intro: 'Použijte uživatelské jméno nebo e-mail a heslo ze současného Torlinu.',
    login: 'Uživatelské jméno nebo e-mail',
    emailSetupTitle: 'Doplnění e-mailu',
    emailSetupIntro: 'Heslo je správné, ale váš účet nemá nastavený e-mail. Zadejte adresu, na kterou vám pošleme ověřovací kód.',
    email: 'E-mail',
    emailConfirmation: 'E-mail znovu',
    emailSetupAction: 'Odeslat ověřovací kód',
    password: 'Heslo',
    tokenDuration: 'Uložit přihlášení na',
    tokenDays: { one: '1 den', seven: '7 dní', thirty: '30 dní', ninety: '90 dní', year: '1 rok' },
    continue: 'Pokračovat',
    verificationTitle: 'Ověření přihlášení',
    verificationIntro: 'Šestimístný kód jsme poslali na',
    verificationValidity: 'Platí 10 minut.',
    code: 'Ověřovací kód',
    verify: 'Ověřit a pokračovat',
    backToLogin: 'Zpět k přihlášení',
    connectionError: 'Spojení se serverem se nezdařilo. Zkuste to prosím znovu.',
  },
  en: {
    metaTitle: 'Log in',
    metaDescription: 'Log in to the TORLIN application.',
    back: 'Back to home',
    title: 'Log in',
    intro: 'Use the username or email address and password from your current TORLIN account.',
    login: 'Username or email',
    emailSetupTitle: 'Add an email address',
    emailSetupIntro: 'Your password is correct, but your account has no email address. Enter one so we can send you a verification code.',
    email: 'Email',
    emailConfirmation: 'Email again',
    emailSetupAction: 'Send verification code',
    password: 'Password',
    tokenDuration: 'Keep me signed in for',
    tokenDays: { one: '1 day', seven: '7 days', thirty: '30 days', ninety: '90 days', year: '1 year' },
    continue: 'Continue',
    verificationTitle: 'Verify your login',
    verificationIntro: 'We sent a six-digit code to',
    verificationValidity: 'It is valid for 10 minutes.',
    code: 'Verification code',
    verify: 'Verify and continue',
    backToLogin: 'Back to login',
    connectionError: 'Could not connect to the server. Please try again.',
  },
  de: {
    metaTitle: 'Anmelden',
    metaDescription: 'Anmeldung bei der TORLIN Anwendung.',
    back: 'Zurück zur Startseite',
    title: 'Anmelden',
    intro: 'Verwenden Sie Benutzername oder E-Mail-Adresse und das Passwort Ihres bisherigen TORLIN Kontos.',
    login: 'Benutzername oder E-Mail',
    emailSetupTitle: 'E-Mail-Adresse ergänzen',
    emailSetupIntro: 'Ihr Passwort ist korrekt, aber für Ihr Konto ist keine E-Mail-Adresse hinterlegt. Geben Sie eine Adresse für den Bestätigungscode ein.',
    email: 'E-Mail',
    emailConfirmation: 'E-Mail wiederholen',
    emailSetupAction: 'Bestätigungscode senden',
    password: 'Passwort',
    tokenDuration: 'Angemeldet bleiben für',
    tokenDays: { one: '1 Tag', seven: '7 Tage', thirty: '30 Tage', ninety: '90 Tage', year: '1 Jahr' },
    continue: 'Weiter',
    verificationTitle: 'Anmeldung bestätigen',
    verificationIntro: 'Wir haben einen sechsstelligen Code gesendet an',
    verificationValidity: 'Er ist 10 Minuten gültig.',
    code: 'Bestätigungscode',
    verify: 'Bestätigen und fortfahren',
    backToLogin: 'Zurück zur Anmeldung',
    connectionError: 'Die Verbindung zum Server ist fehlgeschlagen. Bitte versuchen Sie es erneut.',
  },
};

export function localeHomePath(locale: Locale): string {
  return locale === 'cs' ? '/' : `/${locale}`;
}

export function localeLoginPath(locale: Locale): string {
  return locale === 'cs' ? '/login' : `/${locale}/login`;
}

export function localePath(locale: Locale, path: string): string {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return locale === 'cs' ? normalized : `/${locale}${normalized}`;
}

export function localeFromPathname(pathname: string): Locale {
  const firstSegment = pathname.split('/')[1];
  return firstSegment === 'en' || firstSegment === 'de' ? firstSegment : 'cs';
}

export function switchPathLocale(pathname: string, locale: Locale): string {
  const currentLocale = localeFromPathname(pathname);
  const withoutLocale = currentLocale === 'cs'
    ? pathname
    : pathname.replace(new RegExp(`^/${currentLocale}(?=/|$)`), '') || '/';

  return locale === 'cs' ? withoutLocale : `/${locale}${withoutLocale === '/' ? '' : withoutLocale}`;
}

export function isLocale(value: string): value is Locale {
  return supportedLocales.includes(value as Locale);
}
